import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify/react";
import { useAppDispatch, useAppSelector } from "../store";
import {
  fetchVideoById,
  selectCurrentVideo,
  selectCurrentVideoLoading,
  selectCurrentVideoError,
  clearCurrentVideo,
} from "../store/content/slice";
import { VideoPlayer } from "../components/video";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorBoundary from "../components/common/ErrorBoundary/ErrorBoundary";
import { ErrorDisplay } from "../components/common/ErrorBoundary";
import { useTheme } from "../contexts/ThemeContext";
import { useDirection } from "../contexts/DirectionContext";
import { VideoThumbnail } from "../components/video";
import { ContentService } from "../services/content";
import { CONTENT_TYPES, CONTENT_SORT_VALUES } from "../constants/content";
import { VideoContent, Content } from "../types/content";
import { MediaSlider } from "../components/media/MediaSlider";
import { Tag } from "../components/common/Tag";
import { formatDuration } from "../utils/time";
import { useBranding } from "../contexts/BrandingContext";
import { formatDate } from "../utils/formatters";
import { SharePopup } from '../components/common/SharePopup';
import { useVideoActions } from '../hooks/useVideoActions';
import { DonateButton } from "../components/common/DonateButton";
import VideoDetailsComponent from "../components/video/VideoDetails";
import { MediaListContainer } from "../components/media";
import SEO from "../components/common/SEO";

const VideoPage: React.FC = () => {
  const { videoId } = useParams<{ videoId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { isRTL } = useDirection();
  const { branding } = useBranding();
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [showShareTooltip, setShowShareTooltip] = useState(false);
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const [resourcesCount, setResourcesCount] = useState(0);
  const [relatedVideos, setRelatedVideos] = useState<VideoContent[]>([]);
  const [isLoadingRelated, setIsLoadingRelated] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreRelated, setHasMoreRelated] = useState(true);
  const ITEMS_PER_PAGE = 20;

  const video = useAppSelector(selectCurrentVideo);
  const isLoading = useAppSelector(selectCurrentVideoLoading);
  const error = useAppSelector(selectCurrentVideoError);

  const {
    isSaved,
    isSharePopupOpen,
    shareOptions,
    handleShare,
    handleSave,
    handleDownload,
    closeSharePopup,
  } = useVideoActions({
    video: video ?? undefined,
  });

  const fetchRelatedVideos = useCallback(async (page: number) => {
    if (!video) return;

    setIsLoadingRelated(true);
    try {
      const service = ContentService.getInstance();

      // First try to fetch topic-related videos
      const topicSearchParams: any = {
        type: CONTENT_TYPES.VIDEO,
        sortBy: CONTENT_SORT_VALUES.CREATED_AT,
        sortType: "desc" as const,
        limit: ITEMS_PER_PAGE,
        page,
      };

      if (video.topics?.length > 0) {
        topicSearchParams["topics"] = video.topics.join(",");
      }

      const topicResponse = await service.searchContent(topicSearchParams);
      let filteredResults = topicResponse.results.filter(
        (item: VideoContent | Content): item is VideoContent =>
          item.type === CONTENT_TYPES.VIDEO &&
          "youtube" in item &&
          item.id !== video.id
      );

      // If we don't have enough results, fetch more without topic filter
      if (filteredResults.length < ITEMS_PER_PAGE) {
        const generalSearchParams = {
          type: CONTENT_TYPES.VIDEO,
          sortBy: CONTENT_SORT_VALUES.CREATED_AT,
          sortType: "desc" as const,
          limit: ITEMS_PER_PAGE - filteredResults.length, // Adjust limit to get remaining items
          page,
        };

        const generalResponse = await service.searchContent(
          generalSearchParams
        );
        const filteredAdditional = generalResponse.results.filter(
          (item: VideoContent | Content): item is VideoContent =>
            item.type === CONTENT_TYPES.VIDEO &&
            "youtube" in item &&
            item.id !== video.id &&
            !filteredResults.some((existing: VideoContent) => existing.id === item.id)
        );

        filteredResults = [...filteredResults, ...filteredAdditional];
      }

      if (page === 1) {
        setRelatedVideos(filteredResults);
      } else {
        setRelatedVideos(prev => [...prev, ...filteredResults]);
      }

      // Calculate if there are more results based on the total from API
      const totalFromApi = topicResponse.total;
      const currentTotal = page * ITEMS_PER_PAGE;
      setHasMoreRelated(currentTotal < totalFromApi);
    } catch (error) {
      console.error("Failed to fetch related videos:", error);
      setHasMoreRelated(false);
    } finally {
      setIsLoadingRelated(false);
    }
  }, [video]);

  useEffect(() => {
    if (videoId) {
      dispatch(fetchVideoById(videoId));
    }
    return () => {
      dispatch(clearCurrentVideo());
    };
  }, [videoId, dispatch]);

  // Set resources count when video changes
  useEffect(() => {
    // For now, we're assuming there are no resources
    // In a real implementation, you would fetch this from the video object or API
    // Since resources property doesn't exist on VideoContent type, we're setting it to 0
    // This would be replaced with actual resource count in a real implementation
    setResourcesCount(0);
  }, [video]);

  // Fetch related videos when current video changes
  useEffect(() => {
    setCurrentPage(1);
    setRelatedVideos([]); // Clear existing videos when video changes
    fetchRelatedVideos(1);
  }, [video, fetchRelatedVideos]);

  const handleLoadMore = useCallback(() => {
    if (!isLoadingRelated) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchRelatedVideos(nextPage);
    }
  }, [currentPage, isLoadingRelated, fetchRelatedVideos]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <SEO title={t('video.loading')} noindex={true} />
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] p-4">
        <SEO title={t('video.notFound')} noindex={true} />
        <ErrorDisplay
          error={error}
          onRetry={() => videoId && dispatch(fetchVideoById(videoId))}
          className="mb-4"
          compact
        />
        <button
          onClick={() => navigate("/")}
          className="text-primary-500 hover:text-primary-600 transition-colors"
        >
          {t("common.backToHome")}
        </button>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] p-4 text-text-primary">
        <SEO title={t('video.notFound')} noindex={true} />
        <Icon icon="ph:video-camera-slash-thin" className="w-16 h-16 mb-4" />
        <h2 className="text-xl font-semibold mb-2">{t("video.notFound")}</h2>
        <p className="text-text-secondary text-center">
          {t("video.notFoundDescription")}
        </p>
        <button
          onClick={() => navigate("/")}
          className="mt-4 text-primary-500 hover:text-primary-600 transition-colors"
        >
          {t("common.backToHome")}
        </button>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className={`min-h-screen bg-background-primary transition-colors duration-200 ${isRTL ? "rtl" : "ltr"}`}>
        <SEO
          title={video.title}
          description={video.description}
          image={video.thumbnail}
          keywords={[
            ...(video.topics || []),
            'Jewish videos',
            'Torah lessons',
            'Jewish education'
          ]}
        />
        {/* Main Content Area */}
        <div className="w-full mx-auto pb-8 sm:pb-12 lg:pb-16">
          <div className="sm:p-4 sm:pb-0 max-w-[1850px] mx-auto">
            <div className="lg:flex lg:gap-6 lg:px-8 xl:px-16 2xl:px-24">
              {/* Video Player Section */}
              <div className="flex-1">
                <div className="relative sm:rounded-xl sm:overflow-hidden sm:border sm:border-border-primary">
                  <div className="relative w-full aspect-video bg-black sm:rounded-[11px]">
                    <VideoPlayer
                      video={video}
                      autoplay
                    />
                  </div>

                  {/* Resources Toggle Button - Desktop Only */}
                  <button
                    onClick={() => setIsResourcesOpen(true)}
                    className={`
                      hidden lg:flex absolute top-[15%] ${isRTL ? "left-0" : "right-0"}
                      items-center gap-2 px-2 py-4
                      bg-surface-secondary hover:bg-surface-primary
                      text-text-primary
                      ${isRTL ? "border-r" : "border-l"} border-y border-border-primary
                      ${isRTL ? "rounded-tl-lg rounded-bl-lg" : "rounded-tr-lg rounded-br-lg"}
                      transition-all duration-200
                      shadow-lg z-10 group
                      ${isRTL ? "[writing-mode:vertical-lr]" : "[writing-mode:vertical-lr]"} [text-orientation:mixed]
                      ${isRTL ? "rotate-180" : "rotate-180"}
                      ${resourcesCount === 0 ? "opacity-50 cursor-not-allowed pointer-events-none" : ""}
                    `}
                    aria-label={t("video.showResources")}
                    disabled={resourcesCount === 0}
                  >
                    <Icon
                      icon="ph:files-thin"
                      className={`w-5 h-5 ${isRTL ? "rotate-90" : "rotate-90"}`}
                    />
                    <span className="text-sm font-medium flex items-center gap-2">
                      {t("video.resources")}
                      <span className="text-text-secondary opacity-70 font-normal">
                        ({resourcesCount})
                      </span>
                    </span>
                  </button>

                  {/* Resources Panel - Desktop Only */}
                  {isResourcesOpen && (
                    <div
                      className={`
                        hidden lg:block absolute top-0 ${isRTL ? "left-0" : "right-0"} h-full w-[300px]
                        ${theme.mode === "dark" ? "bg-surface-secondary" : "bg-background-secondary"} border-border-primary border
                        ${isRTL ? "rounded-l-xl" : "rounded-r-xl"}
                        transition-transform duration-300 ease-in-out z-20
                      `}
                    >
                      {/* Panel Header */}
                      <div className="h-14 px-6 border-b border-border-primary flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
                          <Icon icon="ph:files-thin" className="w-5 h-5" />
                          {t("video.resources")}
                        </h2>
                        <button
                          onClick={() => setIsResourcesOpen(false)}
                          className="p-2 hover:bg-surface-primary rounded-lg transition-colors"
                          aria-label={t("common.close")}
                        >
                          <Icon icon="ph:x-thin" className="w-5 h-5 text-text-primary" />
                        </button>
                      </div>

                      {/* Panel Content */}
                      <div className="h-[calc(100%-3.5rem)] overflow-auto p-6">
                        <div className="flex flex-col items-center justify-center text-center h-full">
                          <div className="w-16 h-16 mb-4 rounded-full bg-surface-primary flex items-center justify-center">
                            <Icon icon="ph:files-thin" className="w-8 h-8 text-text-secondary" />
                          </div>
                          <p className="text-text-secondary font-medium mb-2">
                            {t("video.noResources")}
                          </p>
                          <p className="text-sm text-text-tertiary">
                            {t("video.resourcesDescription")}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Video Details Section - Below video on mobile, next to video on desktop */}
                <div className="px-4 sm:px-0 mt-4">
                  <VideoDetailsComponent
                    video={video}
                    layout="default"
                    onShare={handleShare}
                    onSave={handleSave}
                    onDownload={handleDownload}
                    isSaved={isSaved}
                    className="!px-0"
                    resourcesSection={
                      <div>
                        <button
                          onClick={() => setIsResourcesOpen(!isResourcesOpen)}
                          className={`w-full flex items-center justify-between p-4 bg-surface-secondary rounded-xl border border-border-primary ${resourcesCount === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
                          disabled={resourcesCount === 0}
                        >
                          <div className="flex items-center gap-2">
                            <Icon icon="ph:files-thin" className="w-4 h-4" />
                            <span className="text-sm font-medium flex items-center gap-2">
                              {t("video.resources")}
                              <span className="text-text-secondary opacity-70 font-normal">
                                ({resourcesCount})
                              </span>
                            </span>
                          </div>
                          <Icon
                            icon={isResourcesOpen ? "ph:caret-up-thin" : "ph:caret-down-thin"}
                            className="w-4 h-4"
                          />
                        </button>

                        {/* Mobile Resources Panel */}
                        <div className={`mt-2 transition-all duration-300 ease-in-out origin-top ${isResourcesOpen ? "max-h-[300px] opacity-100 scale-y-100" : "max-h-0 opacity-0 scale-y-0"} overflow-hidden`}>
                          <div className="bg-surface-secondary rounded-xl border border-border-primary p-4">
                            <div className="text-center py-4">
                              <div className={`w-12 h-12 mx-auto mb-3 rounded-full ${theme.mode === "dark" ? "bg-surface-primary" : "bg-surface-secondary"} flex items-center justify-center`}>
                                <Icon icon="ph:files-thin" className="w-6 h-6 text-text-secondary" />
                              </div>
                              <p className="text-text-secondary font-medium mb-1">{t("video.noResources")}</p>
                              <p className="text-sm text-text-tertiary">{t("video.resourcesDescription")}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    }
                  />
                </div>
              </div>

              {/* Related Videos Sidebar - Hidden on mobile */}
              <div className="hidden lg:block w-[400px] shrink-0">
                <div className={`${theme.mode === "dark" ? "bg-surface-secondary" : "bg-background-secondary"} rounded-xl border border-border-primary overflow-hidden sticky top-4`}>
                  <MediaListContainer
                    title={t("video.relatedVideos")}
                    items={relatedVideos}
                    loading={isLoadingRelated}
                    type="video"
                    onLoadMore={handleLoadMore}
                    hideLoadMore={!hasMoreRelated}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Related Videos Section - Mobile Only */}
        <div className="lg:hidden mt-6">
          <MediaListContainer
            title={t("video.relatedVideos")}
            items={relatedVideos}
            loading={isLoadingRelated}
            type="video"
            onLoadMore={handleLoadMore}
            hideLoadMore={!hasMoreRelated}
            className="sm:rounded-xl rounded-none border-x-0 sm:border-x"
          />
        </div>

        <SharePopup
          isOpen={isSharePopupOpen}
          onClose={closeSharePopup}
          title={video?.title}
          url={window.location.href}
          shareOptions={shareOptions}
        />
      </div>
    </ErrorBoundary>
  );
};

export default VideoPage;